import { useState, useEffect, useMemo } from 'react'
import {
  EditorHeader,
  InsertMenu,
  PropertiesMenu,
  FullscreenModal,
  Pintura,
  SlideoutMenu,
  Loading,
  EditUnauthorized,
} from 'components'
import { Canvas } from '@/components/editor/Canvas/Canvas'
import { EditorEnhanceProvider } from '@/components/editor/EditorEnhanceProvider/EditorEnhanceProvider'
import { warmTextBoxBaseFace } from 'utils/editor/textBoxFaces'
import Flex from 'components/priority/Flex/Flex'
import {
  useUpdatePageItems,
  useNavigate,
  useResetEditorAtoms,
  useDynamicStyles,
  useStonlyWidget,
  useTokenValidation,
  useLayoutUsedFonts,
} from 'hooks'
import { useCaseInfo } from 'hooks/utility/useCaseInfo'
import {
  editorLoadFailedKeyAtom,
  editorPageSetTurnedOverPromptAtom,
  editorAccessLostPromptAtom,
  editorPagesAtom,
  editorSaveVersionSupportedAtom,
  serviceInformationTemplatesAtom,
} from '@/atoms/_editorAtoms'
import {
  caseSelector,
  itemIdAtom,
  showPinturaAtom,
  unsavedChangesAtom,
  dateFormatAtom,
  editorLayoutAtom,
  editorProductIdAtom,
  editorOrderPageSetAtom,
  triggerUseEffect,
  showUnsavedModalAtom,
  backgroundImageAtom,
  themeOverrideAtom,
  designAssetsAtom,
  themeIdAtom,
  roleAtom,
  itemsLockedAtom,
  stylizedVersesAtom,
  orderIdAtom,
  caseAtom,
  orderAtom,
  applicationTypeAtom,
  outsideCanvasItemsVisibleAtom,
} from 'atoms'
import { pageSetLocaleAtom, baseLayoutAtom } from '@/atoms/_editorAtoms'
import { caseImagesAtom } from '@/atoms/caseImageAtoms'
import { productTabAtom } from '@/atoms/_printPackageAtoms'
import { selectedThemeAtom, themesArrayAtom } from '@/atoms/printPackageAtoms'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { useLocation } from 'react-router-dom'
import { editorRemintedItemIdsAtom } from '@/atoms/editorTextBoxAtoms'
import { deCollideEditorItemIds } from '@/utils/editor/editorItemIds'
import { UnsavedModal } from 'components'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { set } from 'lodash'
import editorGlobal from 'styles/editorGlobal'
import buildUrlWithParams from 'utils/global/buildUrlWithParams'
import { leaveForReturnUrl } from '@/utils/editor/returnToOrderUrl'
import funeralHomeDateFormat from 'utils/dates/funeralHomeDateFormat'
import zLayers from 'utils/styling/zLayers'
import { colors } from '@/utils/styling/colors'
import lockedPallete from 'utils/styling/lockedPalette'
import { EventCategory, logger } from 'utils/logger'
import { useRecoilLogData } from 'utils/recoilLogger'
import { setEditorHeaderCenterText } from 'utils/editor/setEditorHeaderCenterText'
import { getThemeOverrideProductId } from 'utils/editor/themeOverrideProductId'
import { usePostHog } from 'posthog-js/react'
import {
  initializeSessionRecording,
  ensureSessionRecording,
} from 'utils/global/posthog'
import { useTranslation } from 'react-i18next'
import { getItemDisplayName } from 'utils/global/getItemDisplayName'
import { usePageLoadImageQualityCheck } from '@/hooks/editor/usePageLoadImageQualityCheck'
import { useAllPagesImageQualityCheck } from '@/hooks/editor/useAllPagesImageQualityCheck'
import { useFontLoadWarning } from '@/hooks/editor/useFontLoadWarning'
import AllPagesBleedCheck from '@/components/editor/AllPagesBleedCheck'
import CanvasGuard from '@/components/editor/CanvasGuard'
import { FontLoadWarningModal } from '@/components/editor/FontLoadWarningModal/FontLoadWarningModal'
import { EditorPageSetTurnedOverModal } from '@/components/editor/EditorPageSetTurnedOverModal/EditorPageSetTurnedOverModal'
import { EditorAccessLostModal } from '@/components/editor/EditorAccessLostModal/EditorAccessLostModal'
import { countEditorProbeRender } from '@/utils/editor/editorProbe'
import { getFuneralHomeSettings } from '@/api/funeralHomeSettings'
import { getWatermark } from '@/api/themes'
import { getCustomVerses } from '@/api/customVerses'
import { getCaseImageList } from '@/api/caseImages'
import { getDesignAssets } from '@/api/designAssets'
import { getEditorOrderPageDetails } from '@/api/orderPages'
import { useCommitEditorPages } from '@/hooks/editor/useCommitEditorPages'
import {
  disarmUnsavedChangesWarning,
  useUnsavedChangesWarning,
} from '@/hooks/editor/useUnsavedChangesWarning'
import { updatePageSetByOrderProductId } from '@/api/orderPageSets'
import { M3, LOCALE, isTokenizedEditSession } from '@messenger/consts'
import { getServiceInformationTemplates } from '@/api/serviceInformationTemplates'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { useEditorLotusAvailabilityLoad } from '@/hooks/editor/useEditorLotusAvailable'

export const Editor = () => {
  countEditorProbeRender('Editor')
  const { t, i18n } = useTranslation()
  const handleError = useErrorHandler('Editor')
  const recoilLogData = useRecoilLogData()
  const [editorData, setEditorData] = useState({})
  const [themeData, setThemeData] = useState({})
  const [pages, setPages] = useRecoilState(editorPagesAtom)
  const [header, setHeader] = useState('')
  const location = useLocation()
  const [caseData, setCase] = useRecoilState(caseSelector)
  const caseInfo = useRecoilValue(caseAtom)
  const setEditorLayout = useSetRecoilState(editorLayoutAtom)
  const setEditorProductId = useSetRecoilState(editorProductIdAtom)
  const setEditorSaveVersionSupported = useSetRecoilState(
    editorSaveVersionSupportedAtom,
  )
  const [editorOrderPageSet, setEditorOrderPageSet] = useRecoilState(
    editorOrderPageSetAtom,
  )
  const [activeProductId, setActiveProductId] = useRecoilState(productTabAtom)
  const itemIds = useRecoilValue(itemIdAtom)
  const setDateFormat = useSetRecoilState(dateFormatAtom)
  const setOrderId = useSetRecoilState(orderIdAtom)
  const [, setInitialLoad] = useState(true)
  const { saveItems } = useUpdatePageItems()
  const { commitEditorPages } = useCommitEditorPages()
  const [showPintura, setShowPintura] = useRecoilState(showPinturaAtom)
  const [itemsLocked, setItemsLocked] = useRecoilState(itemsLockedAtom)
  const [showUnsavedModal, setShowUnsavedModal] =
    useRecoilState(showUnsavedModalAtom)
  const navigate = useNavigate()
  const [goBack, setGoBack] = useState(false)
  const resetEditorValues = useResetEditorAtoms()
  const [showSlideout, setShowSlideout] = useState(false)
  const [showTurnedOverPrompt, setShowTurnedOverPrompt] = useRecoilState(
    editorPageSetTurnedOverPromptAtom,
  )
  const [showAccessLostPrompt, setShowAccessLostPrompt] = useRecoilState(
    editorAccessLostPromptAtom,
  )
  const setEditorLoadFailedKey = useSetRecoilState(editorLoadFailedKeyAtom)
  const [unsavedChanges, setUnsavedChanges] = useRecoilState(unsavedChangesAtom)
  useUnsavedChangesWarning(unsavedChanges)
  useEffect(() => {
    void warmTextBoxBaseFace()
  }, [])
  const [isLoading, setIsLoading] = useState(true)
  const trigger = useRecoilValue(triggerUseEffect)
  const setBackgroundImage = useSetRecoilState(backgroundImageAtom)
  const setThemeOverride = useSetRecoilState(themeOverrideAtom)
  const setThemeId = useSetRecoilState(themeIdAtom)
  const setSelectedTheme = useSetRecoilState(selectedThemeAtom)
  const setThemesArray = useSetRecoilState(themesArrayAtom)
  const setDesignAssets = useSetRecoilState(designAssetsAtom)
  const setStylizedVerses = useSetRecoilState(stylizedVersesAtom)
  const setCaseImages = useSetRecoilState(caseImagesAtom)
  const setServiceInformationTemplates = useSetRecoilState(
    serviceInformationTemplatesAtom,
  )
  const userRole = useRecoilValue(roleAtom)
  const isAdmin = useIsAdmin()
  const outsideCanvasItemsVisible = useRecoilValue(
    outsideCanvasItemsVisibleAtom,
  )
  const setPageSetLocale = useSetRecoilState(pageSetLocaleAtom)
  const [applicationType, setApplicationType] =
    useRecoilState(applicationTypeAtom)
  const setBaseLayout = useSetRecoilState(baseLayoutAtom)
  const setRemintedItemIds = useSetRecoilState(editorRemintedItemIdsAtom)
  const { pathname, search } = location
  const params = useMemo(() => new URLSearchParams(search), [search])
  const posthog = usePostHog()
  const setOrder = useSetRecoilState(orderAtom)
  const order = useRecoilValue(orderAtom)

  const isMessengerAccess = isTokenizedEditSession(search)

  const editorWrapperBackground = colors(
    'gray5',
    outsideCanvasItemsVisible ? 50 : 100,
  )

  const { isValidSession, returnUrl, shouldCloseTab } =
    useTokenValidation(isMessengerAccess)

  // Get the current application type (combines atom value with query param check for immediate access)
  const currentApplicationType = useMemo(() => {
    const hasM3OrderProductId = params.has('m3OrderProductId')
    return hasM3OrderProductId ? M3 : applicationType
  }, [params, applicationType])

  // Fallback check: if m3OrderProductId is present and applicationType is not M3, set it to M3
  useEffect(() => {
    const hasM3OrderProductId = params.has('m3OrderProductId')
    if (hasM3OrderProductId && applicationType !== M3) {
      setApplicationType(M3)
    }
  }, [params, applicationType, setApplicationType])

  useCaseInfo(caseInfo)
  useDynamicStyles(editorGlobal)
  useStonlyWidget()
  useLayoutUsedFonts()
  useEditorLotusAvailabilityLoad()
  useAllPagesImageQualityCheck({
    enabled: !isLoading,
    debounceMs: 500,
  })
  usePageLoadImageQualityCheck({
    checkDelayMilliseconds: 1000,
    enabled: !isLoading,
  })
  const fontLoadWarning = useFontLoadWarning()

  const fetchWatermarks = async (watermarkId, watermarksArray) => {
    const { data: watermark } = await getWatermark(watermarkId)
    //set the required data fields and do the math to translate them to 96dpi
    //TODO: once the old site is decommissioned we can run a script to do this globally
    watermark.type = 'watermark'
    watermark.y = (watermark.top / 72) * 96
    watermark.x = (watermark.left / 72) * 96
    watermark.height = (watermark.height / 72) * 96
    watermark.width = (watermark.width / 72) * 96
    watermark.z = 1
    //add the watermark to the array of watermarks
    watermarksArray.push(watermark)
  }

  const getData = async () => {
    const [, , orderPageSetId] = pathname.split('/')
    try {
      const { data } = await getEditorOrderPageDetails(
        orderPageSetId,
        currentApplicationType,
      )
      setThemeId(data.theme.id)
      setOrderId(data.order.id)
      setOrder(data.order)
      // A backend without the pre-save route says nothing here, and this stays
      // false: the Save then records a version per page write.
      setEditorSaveVersionSupported(data.supports_editor_save_version === true)
      const designAssetsData = await getDesignAssets(data.theme.id)
      setDesignAssets(designAssetsData.data.designAssets)
      const themeOverride = data?.theme_override?.[0]
      const themeOverrideBaseProductId =
        getThemeOverrideProductId(themeOverride)
      const stylizedVersesData = await getCustomVerses(
        themeOverride?.theme_id,
        themeOverrideBaseProductId,
        data?.product?.id,
      )
      setStylizedVerses(stylizedVersesData.data)
      setEditorLayout(data?.layout?.layout_id)
      setBaseLayout(data?.layout)
      setEditorProductId(data?.product?.id)
      const productId = themeOverrideBaseProductId || data?.product?.product_id
      const serviceInformationTemplatesData = productId
        ? await getServiceInformationTemplates(productId)
        : { data: [] }
      setServiceInformationTemplates(serviceInformationTemplatesData.data)

      const shouldUpdateActiveProductId =
        activeProductId !== data?.product?.id || !activeProductId

      if (shouldUpdateActiveProductId) {
        setActiveProductId(data?.product?.id)
      }

      const { data: settings } = await getFuneralHomeSettings()
      const fetchedWatermarks = []

      //fetch the watermarks if they exist
      if (data?.theme_override?.length > 0) {
        const { theme_override } = data

        if (theme_override?.[0]?.first_watermark)
          await fetchWatermarks(
            theme_override?.[0]?.first_watermark,
            fetchedWatermarks,
          )

        if (theme_override?.[0]?.second_watermark)
          await fetchWatermarks(
            theme_override?.[0]?.second_watermark,
            fetchedWatermarks,
          )
      }

      const format = funeralHomeDateFormat(settings.date_format)
      setDateFormat(format)

      const pageData = []

      // Item ids share one keyspace across the line's pages; the ids minted
      // here are new to the stored page JSON until a Save writes it.
      const seenItemIds = new Set()
      const remintedItemIds = []

      data.pages.map((page) => {
        const { json_template } = page

        if (!json_template?.product?.themeOverride?.themeId) {
          set(json_template, 'product.themeOverride.themeId', data.theme.id)
        }
        const items = json_template?.product?.items
        if (items)
          remintedItemIds.push(...deCollideEditorItemIds(items, seenItemIds))
        // A row saved without an items key (a wiped page) must load as an
        // empty array, never as `undefined`: an undefined slot skips the
        // canvas hydration in Page.js (`if (product?.items)`), which strands
        // the canvas on the previous page and re-serializes the wipe on every
        // subsequent save (JSON.stringify drops undefined-valued keys).
        json_template.product.items = items ?? []
        //set a variable to check for watermark matches
        let matchedMarks
        //if the json template has watermarks and is page 1 (I think interior pages do not have watermarks 🤔)
        if (
          json_template?.product?.watermarks?.length > 0 &&
          json_template.pageProperties.pageNumber === 1
        ) {
          //check to see if the currently set watermarks match the incoming watermarks
          //we want to see if these are the same watermarks as on the currently selected theme
          //we'll want to set some values in the future to track if they replaced the watermarks (then the ids won't match but we won't want to update them)
          //TODO - create logic for handling replaced watermarks and replaced 1 watermark
          matchedMarks = fetchedWatermarks.find((mark) => {
            return json_template.product.watermarks.find(
              (storedMark) => storedMark.id === mark.id,
            )
          })
        }
        //if there are no matches (the watermarks are new to this package) and it's page 1, set the watermarks to be the ones fetched from the db
        if (!matchedMarks && json_template.pageProperties.pageNumber === 1) {
          json_template.product.watermarks = fetchedWatermarks
        }
        //we need the order page id more easily accessible for save, maybe backend can do this but no time for that now in budget
        json_template.product.orderPageId = page.id

        pageData.push(page.json_template)
        return page
      })
      setRemintedItemIds(
        Object.fromEntries(remintedItemIds.map((id) => [id, true])),
      )

      setCase(data.case)

      // Populate case images from the CaseImage table
      if (data.case?.id) {
        try {
          const caseImages = await getCaseImageList(data.case.id)
          setCaseImages(caseImages)
        } catch (err) {
          logger.error({
            message: 'Failed to load case images',
            category: EventCategory.ERROR,
            data: { caseId: data.case.id, error: err?.message || String(err) },
          })
        }
      }

      // Populate themes array from case data for per-product theme resolution.
      // The editor endpoint may override accent fields on `data.theme` based on
      // the user's gallery (DYNAMIC_CONTENT) selection — merge that override
      // into the themes array so the per-product theme selector picks it up.
      if (data.case?.themes?.length > 0) {
        const overriddenThemes = data.theme?.id
          ? data.case.themes.map((t) =>
              t.id === data.theme.id ? { ...t, ...data.theme } : t,
            )
          : data.case.themes
        setThemesArray(overriddenThemes)
      } else if (data.theme) {
        setThemesArray([data.theme])
      }

      setPageSetLocale(data?.page_set?.language || LOCALE.EN)

      const sortedPages = pageData.sort(
        (a, b) => a.pageProperties.pageNumber - b.pageProperties.pageNumber,
      )

      const productFontColor = data?.product?.font_color
      const themeFontColor = data?.theme?.font_color
      const defaultFontColor = lockedPallete.lockedBlack.default
      const resolvedFontColor =
        productFontColor || themeFontColor || defaultFontColor
      const resolvedThemeData = {
        ...data.theme,
        font_color: resolvedFontColor,
      }
      setEditorData(data)
      setThemeData(resolvedThemeData)
      setSelectedTheme(resolvedThemeData)
      setBackgroundImage(data.theme?.image)
      setPages(sortedPages)
      setInitialLoad(false)
      setEditorOrderPageSet(data.page_set)
      setThemeOverride(data?.theme_override?.[0])
      setIsLoading(false)
      ensureSessionRecording(posthog)
    } catch (err) {
      handleError(err?.response?.data?.message || err.message, err)
      setIsLoading(false)
      // This load never reports the editor ready; whatever waits for it
      // reads its end here.
      setEditorLoadFailedKey(trigger)
    }
  }

  useEffect(() => {
    const headerCenterText = setEditorHeaderCenterText({
      nameOfDeceased: caseData?.name_of_deceased,
      productName: getItemDisplayName(editorData?.product?.name, 'products', t),
      layoutName: editorData?.layout?.name,
      themeName: getItemDisplayName(editorData?.theme?.name, 'themes', t),
    })

    if (headerCenterText) {
      setHeader(headerCenterText)
    }
  }, [caseData, editorData, t])

  useEffect(() => {
    resetEditorValues()
    getData()
    //eslint-disable-next-line
  }, [trigger])

  useEffect(() => {
    if (goBack) {
      const orderId = editorData?.order?.id ?? order?.id
      if (!orderId) {
        setGoBack(false)
        return
      }
      // The clipboard stays filled, so items copied here can be pasted into
      // the next layout the designer opens without a reload.
      navigate(buildUrlWithParams(`/package/${orderId}/customize`, params))
      setGoBack(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goBack])

  const updateOrderPageSetByLayoutId = async () => {
    try {
      const isEditorOrderPageSetEmpty =
        !editorOrderPageSet ||
        Object.keys(editorOrderPageSet || {}).length === 0

      if (isEditorOrderPageSetEmpty) return
      const { id } = editorOrderPageSet
      await updatePageSetByOrderProductId(
        editorData.product.id,
        {
          currentOrderPageSetIdUsed: id,
        },
        currentApplicationType,
      )
    } catch (error) {
      logger.error({
        message: 'editor_update_order_page_set_by_layout_id_failed',
        category: EventCategory.ERROR,
        error: new Error(
          `Failed to update order page set by layout id: ${
            error?.response?.data?.message || error.message
          }`,
        ),
        data: {
          ...recoilLogData,
        },
      })
    }
  }

  // The pages this tab is showing were replaced somewhere else. The commit
  // has already said so once and dropped the drafts for them, so all that is
  // left is to land somewhere that loads: the order's customize page on an
  // MDL line, and for an M3 line the case's site, or a closed tab when the
  // case has no return URL.
  const exitReplacedPages = () => {
    // The pages still on the line were written before this ran, and what is
    // left belonged to pages the line no longer has. Holding the guard armed
    // would put the browser's leave prompt in front of an M3 tab on its way
    // out, over work nothing can save.
    setUnsavedChanges(false)
    if (currentApplicationType === M3) {
      // The navigation starts now, before React commits the state above and
      // the hook's own cleanup runs, so the listener is removed by hand.
      disarmUnsavedChangesWarning()
      leaveForReturnUrl(caseInfo?.return_url, i18n.language)
      return
    }
    setGoBack(true)
  }

  const update = () =>
    commitEditorPages({
      flushCanvas: saveItems,
      applicationType: currentApplicationType,
      updatePageSetPointer: updateOrderPageSetByLayoutId,
      onStalePages: exitReplacedPages,
      onTurnedOverPages: () => setShowTurnedOverPrompt(true),
    })

  const back = () => {
    if (!unsavedChanges) {
      const orderId = editorData?.order?.id ?? order?.id
      if (!orderId) return
      setGoBack(true)
      logger.info({
        message: 'editor_back_navigation',
        category: EventCategory.NAVIGATION,
        data: {
          ...recoilLogData,
          navigatedTo: buildUrlWithParams(
            `/package/${orderId}/customize`,
            params,
          ),
        },
      })
    } else {
      setShowUnsavedModal(true)
      logger.info({
        message: 'editor_unsaved_changes_modal_shown',
        category: EventCategory.SHOW,
        data: {
          ...recoilLogData,
        },
      })
    }
  }

  useEffect(() => {
    if (posthog) {
      initializeSessionRecording(posthog)
    }
  }, [posthog])

  useEffect(() => {
    const roleIsLoaded = userRole !== 'loading'
    if (roleIsLoaded) {
      const isM3 = currentApplicationType === M3
      const shouldLockItems = !isAdmin && !isM3
      setItemsLocked(shouldLockItems)
    }
  }, [userRole, isAdmin, setItemsLocked, currentApplicationType])

  // Show loading state while checking validation
  if (isValidSession === null) {
    return null
  }

  // Show unauthorized if validation failed
  if (isValidSession === false) {
    return (
      <EditUnauthorized
        returnUrlProp={returnUrl}
        shouldCloseTab={shouldCloseTab}
      />
    )
  }

  return (
    // The provider owns the editor's one Enhance (AI upscale) modal, shared by
    // the Case tab's grid and the low-resolution warnings on canvas items.
    <EditorEnhanceProvider>
      <Flex column height="100vh">
        <FullscreenModal
          hideClose={!showPintura}
          show={[showPintura, setShowPintura]}
        >
          {showPintura && <Pintura update={update} />}
        </FullscreenModal>
        <FullscreenModal show={[showUnsavedModal, setShowUnsavedModal]}>
          <UnsavedModal
            setShow={() => setShowUnsavedModal(false)}
            orderId={editorData?.order?.id}
          />
        </FullscreenModal>
        <EditorPageSetTurnedOverModal
          show={showTurnedOverPrompt}
          onDismiss={() => setShowTurnedOverPrompt(false)}
          reload={() => window.location.reload()}
        />
        <EditorAccessLostModal
          show={showAccessLostPrompt}
          onDismiss={() => setShowAccessLostPrompt(false)}
        />
        <FontLoadWarningModal
          show={fontLoadWarning.show}
          families={fontLoadWarning.families}
          onClose={fontLoadWarning.close}
        />
        <EditorHeader assetDetails={header} update={update} back={back} />
        <AllPagesBleedCheck enabled={!isLoading} debounceMs={500} />
        <Flex
          justify="space-between"
          align={isLoading ? 'center' : 'flex-start'}
          position="relative"
          // When design canvas is visible: 50% opacity over dark body matches GrayoutZone's 2% over white Page.
          // When hidden: both use 100% opacity to fully obscure items outside the product.
          background={editorWrapperBackground}
          height="calc(100vh - 70px)"
          borderTop="1px solid"
          borderColor="gray4"
        >
          <InsertMenu
            basePages={editorData?.pages}
            baseLayout={editorData?.layout}
            setIsLoading={setIsLoading}
            update={update}
            itemsLocked={itemsLocked}
          />

          <SlideoutMenu
            className="slide-out"
            title="Pages"
            left="250px"
            //TODO: move to styled component? there is probably a better way
            style={{ zIndex: zLayers.interface }}
            // Replaces SlideoutMenu's default test id, so that one names only
            // the insert menu's slideout.
            data-testid="page-shuffler-menu"
            show={[showSlideout, setShowSlideout]}
            menu="PageShuffler"
            theme={themeData}
            themeOverride={editorData?.theme_override?.[0]}
            basePages={editorData?.pages}
            baseLayout={editorData?.layout}
          />

          <Loading isLoading={isLoading}>
            {pages.length > 0 && (
              <CanvasGuard>
                <Canvas
                  id={itemIds[0]}
                  theme={themeData}
                  themeOverride={editorData?.theme_override?.[0]}
                  loadedPages={pages}
                  setShowSlideout={setShowSlideout}
                  baseLayout={editorData?.layout}
                />
              </CanvasGuard>
            )}
          </Loading>
          <PropertiesMenu />
        </Flex>
      </Flex>
    </EditorEnhanceProvider>
  )
}

export default Editor
